/**
 * /decision-log-template — SEO landing page targeting the "decision log
 * template" / "decision log example" / "decision record template"
 * cluster.
 *
 * Differentiation angle: a decision log template in Notion / Confluence /
 * markdown is just a fill-in-the-blanks format — the moment you finish
 * filling it in, it starts going out of date, because nothing connects a
 * field to what depends on it. This page hands the reader the actual
 * template (the eight fields, spelled out) so a searcher gets something
 * usable, then shows that Draftlize turns each field into a structured
 * card where dependents auto-flag stale when an upstream decision changes.
 * Same family as an architecture decision record (ADR).
 *
 * English-only + inline (no i18n), mirroring DecisionLog.tsx and the
 * comparison pages. Chinese variant lives in DecisionLogTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function DecisionLogTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/decision-log-template"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="Decision Log Template (That Doesn't Go Stale) · Draftlize"
      documentDescription="A decision log template you can copy — Decision, Context, Options, Rationale, Dependencies, Status, Owner, Date. The catch: a template in Notion or markdown is stale the moment you fill it in. In Draftlize every field is a structured card, and dependents auto-flag stale when an upstream decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Decision Logs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A decision log template<br/>
            <em>that updates itself.</em>
          </h1>
          <p className="lede">
            You came for a <em>decision log template</em> — the fields, the headings, something to paste into a doc. It's below; take it. But know what you're getting: a template is a fill-in-the-blanks format, and the instant you finish filling it in, it starts going out of date. Nothing in a Notion table or a markdown file connects a decision to the specs that depend on it, so when the decision changes, the log quietly becomes fiction. Draftlize gives you the same eight fields — and makes each one a card an AI agent reads and writes, where everything downstream flags itself stale the moment one decision moves.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight fields. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole template. It works in a doc, a wiki, or a markdown file — it's the same shape as an architecture decision record (ADR). Use it as-is. The next section is about what changes when each field stops being text and becomes a card.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Decision',
              hint: 'Required',
              body: 'The call, stated in one line. "We charge per use, not per seat." If you can\'t compress it to a sentence, you haven\'t decided yet.',
            },
            {
              k: 'Context',
              hint: 'Why now',
              body: 'The situation that forced the choice. What was true, what was constrained, what pressure made this a decision rather than a default.',
            },
            {
              k: 'Options considered',
              hint: 'The forks',
              body: 'The alternatives that were real, not strawmen. The value of a decision log is mostly here — it stops you re-litigating a path you already rejected, with reasons.',
            },
            {
              k: 'Rationale',
              hint: 'The why',
              body: 'Why this option won and the others lost. The single field your future self actually opens the log to read. Be honest about the trade you accepted.',
            },
            {
              k: 'Dependencies',
              hint: 'What rests on this',
              body: 'The specs, decisions, and work that assume this holds. In a doc this is a list you\'ll forget to update. It\'s the field that decides whether the log stays true.',
            },
            {
              k: 'Status',
              hint: 'State',
              body: 'Proposed, accepted, superseded — or stale. A decision isn\'t permanent; the log has to be able to say "this no longer holds" without deleting the history of why it once did.',
            },
            {
              k: 'Owner',
              hint: 'Who',
              body: 'The person to ask when the rationale stops making sense. Not for blame — for the follow-up question six months out.',
            },
            {
              k: 'Date',
              hint: 'When',
              body: 'When it was decided. Cheap to record, and the thing that lets you read decisions in the order they actually happened.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why filling it in is the moment it starts to rot — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a template <em>goes stale.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">A template is a format, not a system</h3>
            <p>The eight fields are good — that's why ADRs have used roughly this shape since 2011. But a format only captures the decision once. It has no idea when the thing it recorded stops being true, because a table of text can't watch the rest of the product change.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The Dependencies field is a promise you'll break</h3>
            <p>You dutifully list what depends on a decision. Then the decision changes, and updating those dependents is now manual archaeology — find every spec that assumed the old call, by memory. Nobody does it. The field that was supposed to keep the log honest is the first to lie.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Filled in is the high-water mark</h3>
            <p>The template is most accurate the minute after you finish it, and never that accurate again. By day ninety a real fraction of it is wrong, and you can't tell which fraction — so the whole log gets quietly distrusted, which is worse than not having one.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each field becomes a card, not a cell</h3>
            <p>In Draftlize the template isn't a blank table — it's a typed card with those exact fields: Decision, Context, Options, Rationale, Dependencies, Status, Owner, Date. Structured, addressable by ID, and citable from a spec or a thread instead of buried in a wiki page.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one decision, dependents flag stale</h3>
            <p>Because Dependencies is a real link and not a list of nouns, changing a decision turns every card that rested on it stale automatically — the way a build system invalidates everything downstream of a changed file. The promise the template made, the substrate keeps.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes it</h3>
            <p>Claude Code or Cursor, over MCP, reads every relevant decision before it drafts the next spec and writes new decisions back into the same shape. The log stops being a document for a reader who never returns and becomes context that gets read on every turn.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A template tells you what to write down. It can't tell you when what you wrote stopped being true.
        </blockquote>
        <cite>Keep the eight fields. Let the substrate keep them current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a decision log?', a: 'A decision log is a running record of the significant choices a team made, why they made them, and what was decided against — so the reasoning survives past the meeting and nobody re-litigates a settled call six months later. Each entry typically captures the decision, context, options considered, rationale, dependencies, status, owner, and date.' },
            { q: 'What is the difference between a decision log and an ADR?', a: 'An architecture decision record (ADR) is a decision log specialised for engineering choices — same eight-field shape, narrower scope. A decision log covers product, pricing, and strategy calls too. If you already write ADRs, a decision log is the same discipline applied to the decisions upstream of the architecture.' },
            { q: 'What should each decision log entry include?', a: 'Decision (the call in one line), context (why now), options considered, rationale (why this won), dependencies (what rests on it), status (proposed / accepted / superseded / stale), owner, and date. The rationale and the rejected options are the fields your future self actually opens the log to read.' },
            { q: 'Where should a team keep its decision log, and why do most go stale?', a: 'Anywhere text lives — a wiki, a markdown file, a Notion table. They go stale because nothing connects a logged decision to the specs that depend on it, so when the decision changes the log quietly becomes fiction. Draftlize keeps the same eight fields as cards and auto-flags every dependent stale the moment a decision moves, so the log updates itself instead of rotting.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your decision log <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent fill it in from your existing docs — then keep every decision, and everything that depends on it, current for you.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="decision-log" />
    </MarketingShell>
  );
}
